import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import Image from 'next/image'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import closeIcon from 'assets/svg/closeIcon.svg'
import playIcon from 'assets/svg/play.svg'
import uploadIcon from 'assets/svg/upload.svg'

import { WEB_CONSEST_LINK_SC, WEB_POLICY_LINK_SC } from '@/shared/lib/utils/constants/pdfDocs'
import { Button } from '@/shared/ui'
import { Icon } from '@/shared/ui/icon'
import { Notification } from '@/shared/ui/Notification'
import { Switch } from '@/shared/ui/switch'

import { useCreateProductReviewMutation, useUpdateProductReviewMutation } from '../../api/product-reviews-api'
import { isReviewVideo, resolveReviewMediaUrl } from '../../lib/media'
import { MAX_REVIEW_ATTACHMENTS, RATING_LABELS } from '../../model/constants'
import { ProductReview, ProductReviewsApiError } from '../../model/types'
import { type ReviewAttachment, useReviewAttachments } from '../../model/use-review-attachments'
import { Stars } from '../stars/Stars'

import styles from './ReviewForm.module.scss'

type ReviewFormProps = {
	productCode: number
	productName?: string
	token?: string
	review?: ProductReview
	onOpenModerationRules?: () => void
	onSuccess: (rating: number, review?: ProductReview) => void
}

const reviewFormSchema = z.object({
	rating: z.custom<number>(value => typeof value === 'number' && value >= 1 && value <= 5, 'Выберите оценку.'),
	comment: z.string().trim().min(1, 'Добавьте комментарий.'),
	isAnonymous: z.boolean()
})

type ReviewFormValues = z.infer<typeof reviewFormSchema>

const MAX_COMMENT_FIELD_HEIGHT = 250

const resizeCommentTextarea = (textarea: HTMLTextAreaElement | null): void => {
	if (!textarea) {
		return
	}

	textarea.style.height = 'auto'
	textarea.style.height = Math.min(textarea.scrollHeight, MAX_COMMENT_FIELD_HEIGHT) + 'px'
	textarea.style.overflowY = textarea.scrollHeight > MAX_COMMENT_FIELD_HEIGHT ? 'auto' : 'hidden'
}

const getReviewAttachmentName = (src: string, index: number) =>
	src.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || 'media-' + (index + 1)

const getReviewInitialAttachments = (review?: ProductReview) =>
	(review?.media?.length
		? review.media
		: review?.photos.map(src => ({
				src,
				uuid: ''
		  }))
	)?.map(({ src, uuid }, index) => ({
		name: getReviewAttachmentName(src, index),
		preview: resolveReviewMediaUrl(src) ?? src,
		type: isReviewVideo(src) ? ('video' as const) : ('image' as const),
		isExisting: true,
		mediaUuid: uuid
	})) ?? []

const getRemovedReviewMediaUuids = (review: ProductReview | undefined, attachments: ReviewAttachment[]) => {
	const currentMediaUuids = new Set(
		attachments.flatMap(attachment => (attachment.mediaUuid ? [attachment.mediaUuid] : []))
	)

	return review?.media.map(item => item.uuid).filter(uuid => uuid && !currentMediaUuids.has(uuid)) ?? []
}
export const ReviewForm = ({
	productCode,
	productName,
	token,
	review,
	onOpenModerationRules,
	onSuccess
}: ReviewFormProps) => {
	const [createProductReview] = useCreateProductReviewMutation()
	const [updateProductReview] = useUpdateProductReviewMutation()
	const [error, setError] = useState('')
	const commentTextareaRef = useRef<HTMLTextAreaElement | null>(null)
	const {
		control,
		clearErrors,
		handleSubmit,
		reset,
		formState: { isSubmitting }
	} = useForm<ReviewFormValues>({
		resolver: zodResolver(reviewFormSchema),
		mode: 'onBlur',
		defaultValues: {
			rating: review?.rating,
			comment: review?.comment ?? '',
			isAnonymous: false
		}
	})
	const {
		attachments,
		attachmentError,
		fileInputRef,
		addFiles,
		removeAttachment,
		resetAttachments,
		openFileDialog,
		handleUploadKeyDown
	} = useReviewAttachments()
	const hasAttachments = attachments.length > 0
	const canAddAttachment = attachments.length < MAX_REVIEW_ATTACHMENTS

	useEffect(() => {
		reset({
			rating: review?.rating,
			comment: review?.comment ?? '',
			isAnonymous: false
		})
		resetAttachments(getReviewInitialAttachments(review))
		setError('')
		resizeCommentTextarea(commentTextareaRef.current)
	}, [productCode, reset, resetAttachments, review])

	const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>, onChange: (value: string) => void): void => {
		onChange(event.target.value)
		clearErrors('comment')
		resizeCommentTextarea(event.target)
	}

	const onSubmit = async ({ rating, comment, isAnonymous }: ReviewFormValues): Promise<void> => {
		setError('')
		try {
			const clearMedia = getRemovedReviewMediaUuids(review, attachments)
			const payload = {
				productCode,
				productName: productName ?? '',
				rating,
				criteria: [],
				comment: comment.trim(),
				isAnonymous,
				attachments: attachments.flatMap(attachment => (attachment.file ? [attachment.file] : [])),
				token
			}
			const savedReview = review
				? await updateProductReview({ ...payload, clearMedia, reviewId: review.id }).unwrap()
				: await createProductReview(payload).unwrap()
			reset()
			resetAttachments()
			setError('')
			onSuccess(rating, savedReview)
		} catch (requestError) {
			const queryError = requestError as { data?: ProductReviewsApiError }
			const apiError = queryError.data ?? (requestError as ProductReviewsApiError)
			setError(
				apiError.status === 422
					? apiError.validationMessage || 'Проверьте обязательные поля и ограничения формы.'
					: 'Не удалось сохранить отзыв. Пожалуйста, повторите попытку позже.'
			)
		}
	}

	return (
		<form className={styles.reviewForm} onSubmit={handleSubmit(onSubmit)} noValidate>
			<h2>Оставить отзыв</h2>
			<div className={styles.overallRating}>
				<strong>Ждем вашу оценку</strong>
				<Controller
					name="rating"
					control={control}
					render={({ field, fieldState }) => (
						<>
							<Stars
								rating={field.value}
								onChange={rating => {
									field.onChange(rating)
									clearErrors('rating')
								}}
								size="large"
							/>
							{!fieldState.error && (
								<span className={styles.ratingLabel}>{field.value ? RATING_LABELS[field.value] : ''}</span>
							)}
							{fieldState.error && <span className={styles.fieldError}>{fieldState.error.message}</span>}
						</>
					)}
				/>
			</div>

			<Controller
				name="comment"
				control={control}
				render={({ field, fieldState }) => (
					<label className={styles.commentField}>
						<strong>Добавьте комментарий</strong>
						<span className={styles.commentControl}>
							<textarea
								{...field}
								value={field.value ?? ''}
								ref={element => {
									field.ref(element)
									commentTextareaRef.current = element
									resizeCommentTextarea(element)
								}}
								onChange={event => handleCommentChange(event, field.onChange)}
								placeholder="Расскажите о своих впечатлениях"
								rows={3}
							/>
							{field.value && (
								<button
									type="button"
									onClick={() => {
										field.onChange('')
										clearErrors('comment')
									}}
									aria-label="Очистить комментарий">
									<Icon icon={closeIcon} width={10} height={10} title="" />
								</button>
							)}
						</span>
						{fieldState.error && <span className={styles.fieldError}>{fieldState.error.message}</span>}
					</label>
				)}
			/>

			{review && (
				<Notification
					className={styles.moderationNotice}
					type="info"
					size="sm"
					message={
						<>
							После публикации обновлений отзыв отправится на{' '}
							<button className={styles.moderationRulesButton} type="button" onClick={onOpenModerationRules}>
								модерацию
							</button>
						</>
					}
				/>
			)}

			<div className={styles.uploadField}>
				<strong>Добавьте фото или видео</strong>
				<div className={styles.uploadControls}>
					<div
						className={clsx(styles.uploadBox, hasAttachments && styles.uploadBoxFilled)}
						role={hasAttachments ? undefined : 'button'}
						tabIndex={hasAttachments ? undefined : 0}
						onClick={hasAttachments ? undefined : openFileDialog}
						onKeyDown={hasAttachments ? undefined : handleUploadKeyDown}
						aria-label={hasAttachments ? undefined : 'Добавить фото или видео товара'}>
						{attachments.map((attachment, index) => (
							<span
								className={clsx(styles.attachmentPreview, attachment.type === 'video' && styles.videoPreview)}
								key={`${attachment.name}-${index}`}>
								{attachment.type === 'video' ? (
									<>
										<video src={attachment.preview} muted playsInline preload="metadata" aria-label={attachment.name}>
											<track kind="captions" />
										</video>
										<Icon className={styles.videoPlayIcon} icon={playIcon} width={39} height={39} title="" />
									</>
								) : (
									<Image src={attachment.preview} alt={attachment.name} width={60} height={60} unoptimized />
								)}
								<button
									type="button"
									onClick={event => {
										event.stopPropagation()
										removeAttachment(index)
									}}
									aria-label={`Удалить ${attachment.name}`}>
									<Icon icon={closeIcon} width={8} height={8} title="" />
								</button>
							</span>
						))}
						{canAddAttachment && (
							<span
								className={hasAttachments ? styles.uploadButton : styles.uploadEmpty}
								role={hasAttachments ? 'button' : undefined}
								tabIndex={hasAttachments ? 0 : undefined}
								onClick={hasAttachments ? openFileDialog : undefined}
								onKeyDown={hasAttachments ? handleUploadKeyDown : undefined}
								aria-label={hasAttachments ? 'Добавить еще фото или видео' : undefined}>
								<Icon icon={uploadIcon} width={24} height={24} title="" />
								{!hasAttachments && <span>Добавьте фото или видео товара</span>}
							</span>
						)}
						<input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={addFiles} />
					</div>
					{attachmentError && (
						<p className={styles.fieldError} role="alert">
							{attachmentError}
						</p>
					)}
					<div className={styles.anonymousField}>
						<Controller
							name="isAnonymous"
							control={control}
							render={({ field }) => (
								<Switch
									id="product-review-anonymous"
									checked={field.value}
									onChange={event => field.onChange(event.target.checked)}
								/>
							)}
						/>
						<label htmlFor="product-review-anonymous">Сделать отзыв анонимным</label>
					</div>
				</div>
			</div>

			{error && <p className={styles.formError}>{error}</p>}
			<div className={styles.submitGroup}>
				<Button className={styles.submitButton} type="submit" size="L" fullWidth isLoading={isSubmitting}>
					Отправить
				</Button>
				<p className={styles.legalText}>
					Нажимая кнопку, я подтверждаю свое ознакомление и согласие с{' '}
					<a href={WEB_POLICY_LINK_SC} target="_blank" rel="noreferrer">
						Политикой Конфиденциальности
					</a>
					,{' '}
					<a href={WEB_CONSEST_LINK_SC} target="_blank" rel="noreferrer">
						Согласием на обработку персональных данных
					</a>
					. Я достиг(ла) 18-летнего возраста.
				</p>
			</div>
		</form>
	)
}
